import React, { useEffect, useState, useCallback } from "react";
import { adminApi } from "@/lib/adminApi";
import { AdminPageHeader, Card, Input, Select, Btn, Spinner, ErrorBanner, Pagination, Tag, EmptyState } from "./_shared";
import { Search, CheckCircle2, Trash2, Ban, Star, StarOff, RotateCcw } from "lucide-react";

const CATEGORIES = [
  "", "jobs", "waste-management", "roads", "ai-technology", "education",
  "corruption", "youth-opportunities", "healthcare", "cost-of-living",
  "environment", "public-transport", "other",
];

const STATUS_COLORS = {
  approved: "#138808",
  flagged: "#FF9933",
  removed: "#737373",
  spam: "#E3000F",
  pending: "#9b9b9b",
  featured: "#FFD700",
};

// Sentinel values for the campaign picker
const SCOPE_ALL = "__all__";        // legacy `/admin/messages` view (free-text)

export const Messages = ({ defaultStatus = "all", titleOverride, eyebrowOverride, subOverride, hideStatusFilter = false }) => {
  const [polls, setPolls] = useState([]);
  const [scope, setScope] = useState(SCOPE_ALL); // SCOPE_ALL or a poll slug
  const [status, setStatus] = useState(defaultStatus);
  const [category, setCategory] = useState("");
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("latest");
  const [skip, setSkip] = useState(0);
  const limit = 20;
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [actingId, setActingId] = useState(null);

  // Load polls for the campaign picker (separates MC from open polls)
  useEffect(() => {
    adminApi.get("/admin/polls").then((r) => setPolls(r.data?.polls || [])).catch(() => { /* still works */ });
  }, []);

  // Resolve which endpoint to use based on scope:
  //  - SCOPE_ALL → legacy /admin/messages (db.messages, free-text)
  //  - open-poll slug → /admin/messages?poll_id={slug}
  //  - MC-poll slug → /admin/polls/{slug}/comments (db.poll_comments)
  const selectedPoll = polls.find((p) => p.slug === scope) || null;
  const isMcScope = !!selectedPoll && selectedPoll.poll_type === "multiple_choice";

  const load = useCallback(async () => {
    setErr(null);
    setData(null);
    try {
      if (isMcScope) {
        const params = { status, skip, limit };
        if (q) params.q = q;
        const r = await adminApi.get(`/admin/polls/${scope}/comments`, { params });
        setData({
          messages: (r.data?.items || []).map((c) => ({
            id: c.id,
            message: c.text,
            status: c.status,
            upvotes: c.upvotes || 0,
            category: selectedPoll?.category || "comment",
            profile: c.profile || {},
            created_at: c.created_at,
            featured: c.status === "featured",
            _source: "poll_comment",
          })),
          total: r.data?.total || 0,
        });
      } else {
        const params = { status, sort, skip, limit };
        if (category) params.category = category;
        if (q) params.q = q;
        if (scope !== SCOPE_ALL) params.poll_id = scope; // for open polls
        const r = await adminApi.get("/admin/messages", { params });
        setData(r.data);
      }
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to load messages.");
    }
  }, [scope, isMcScope, status, category, q, sort, skip, selectedPoll]);

  useEffect(() => { load(); }, [load]);

  // Reset filters that don't make sense when switching scope
  useEffect(() => { setSkip(0); }, [scope]);

  const moderate = async (id, action) => {
    setActingId(id);
    try {
      if (isMcScope) {
        await adminApi.post(`/admin/polls/${scope}/comments/${id}/moderate`, { action });
      } else {
        await adminApi.post(`/admin/messages/${id}/moderate`, { action });
      }
      await load();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Action failed.");
    } finally {
      setActingId(null);
    }
  };

  return (
    <div className="space-y-5" data-testid="messages-page">
      <AdminPageHeader
        eyebrow={eyebrowOverride || "INBOX"}
        title={titleOverride || "All Messages"}
        sub={subOverride || "Filter, search, and moderate every campaign's submissions in one place."}
      />

      <Card>
        <div className="grid md:grid-cols-12 gap-3 items-end">
          {/* Campaign picker */}
          <div className="md:col-span-3">
            <label className="block text-[10px] uppercase font-bold tracking-[0.3em] text-neutral-400 mb-2">Choose Campaign</label>
            <Select value={scope} onChange={(e) => { setSkip(0); setScope(e.target.value); }} data-testid="messages-campaign-picker">
              <option value={SCOPE_ALL}>All campaigns (free-text)</option>
              {polls.map((p) => (
                <option key={p.slug} value={p.slug}>
                  {p.poll_type === "multiple_choice" ? "[MC] " : "[Open] "}{p.title || p.headline}
                </option>
              ))}
            </Select>
          </div>

          <div className="md:col-span-3">
            <label className="block text-[10px] uppercase font-bold tracking-[0.3em] text-neutral-400 mb-2">Search</label>
            <div className="relative">
              <Input value={q} onChange={(e) => { setSkip(0); setQ(e.target.value); }} placeholder="Search text…" data-testid="messages-search" className="pl-9" />
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" strokeWidth={3} />
            </div>
          </div>

          {!hideStatusFilter && (
            <div className="md:col-span-2">
              <label className="block text-[10px] uppercase font-bold tracking-[0.3em] text-neutral-400 mb-2">Status</label>
              <Select value={status} onChange={(e) => { setSkip(0); setStatus(e.target.value); }} data-testid="messages-status">
                <option value="all">All</option>
                {isMcScope ? (
                  <>
                    <option value="pending">Pending</option>
                    <option value="approved">Approved</option>
                    <option value="featured">Featured</option>
                    <option value="spam">Spam</option>
                    <option value="removed">Removed</option>
                  </>
                ) : (
                  <>
                    <option value="approved">Approved</option>
                    <option value="flagged">Flagged</option>
                    <option value="removed">Removed</option>
                    <option value="spam">Spam</option>
                  </>
                )}
              </Select>
            </div>
          )}

          {/* Category & sort only apply to free-text messages */}
          {!isMcScope && (
            <>
              <div className="md:col-span-2">
                <label className="block text-[10px] uppercase font-bold tracking-[0.3em] text-neutral-400 mb-2">Category</label>
                <Select value={category} onChange={(e) => { setSkip(0); setCategory(e.target.value); }} data-testid="messages-category">
                  {CATEGORIES.map((c) => <option key={c || "all"} value={c}>{c ? c : "All categories"}</option>)}
                </Select>
              </div>
              <div className="md:col-span-2">
                <label className="block text-[10px] uppercase font-bold tracking-[0.3em] text-neutral-400 mb-2">Sort</label>
                <Select value={sort} onChange={(e) => setSort(e.target.value)} data-testid="messages-sort">
                  <option value="latest">Latest</option>
                  <option value="upvotes">Most upvotes</option>
                </Select>
              </div>
            </>
          )}
        </div>

        {selectedPoll && (
          <p className="mt-3 text-[10px] uppercase font-bold tracking-[0.3em] text-neutral-500" data-testid="messages-scope-hint">
            Scope: {selectedPoll.title || selectedPoll.headline}
            {isMcScope ? " · follow-up comments after voting" : " · free-text messages"}
          </p>
        )}
      </Card>

      {err && <ErrorBanner message={err} />}
      {!data && <Spinner />}

      {data && data.messages.length === 0 && (
        <EmptyState title="No messages match these filters." sub="Try clearing the search, switching status, or picking a different campaign." />
      )}

      {data && data.messages.length > 0 && (
        <Card>
          <ul className="divide-y divide-[#1f1f1f]" data-testid="messages-list">
            {data.messages.map((m) => (
              <li key={m.id} className="py-4 flex flex-col md:flex-row md:items-start md:justify-between gap-3" data-testid={`row-${m.id}`}>
                <div className="flex-1 min-w-0">
                  <p className="text-sm leading-relaxed text-[#FDFBF7]" data-testid={`row-text-${m.id}`}>{m.message}</p>
                  <div className="flex flex-wrap items-center gap-2 mt-2">
                    <Tag color={STATUS_COLORS[m.status] || "#737373"} testId={`row-status-${m.id}`}>{m.status}</Tag>
                    {!isMcScope && m.category && <Tag color="#FF9933">{m.category}</Tag>}
                    <Tag color="#138808">{m.upvotes} upvotes</Tag>
                    {m.featured && <Tag color="#FFD700">Featured</Tag>}
                    {m.profile?.district && <Tag color="#9b9b9b">{m.profile.district}</Tag>}
                    {m.profile?.age_group && <Tag color="#9b9b9b">{m.profile.age_group}</Tag>}
                    {m.profile?.occupation && <Tag color="#9b9b9b">{m.profile.occupation}</Tag>}
                    <span className="text-[10px] uppercase font-bold tracking-[0.25em] text-neutral-500">
                      {m.created_at?.slice(0, 16).replace("T", " ")}
                    </span>
                  </div>
                  {m.flag_reason && (
                    <p className="mt-1 text-[10px] uppercase font-bold tracking-[0.3em]" style={{ color: "#E3000F" }}>
                      Flag: {m.flag_reason}
                    </p>
                  )}
                </div>
                <div className="flex flex-wrap gap-1.5 md:justify-end shrink-0">
                  {m.status !== "approved" && m.status !== "featured" && (
                    <Btn variant="ghost" onClick={() => moderate(m.id, "approve")} disabled={actingId === m.id} data-testid={`act-approve-${m.id}`}>
                      <CheckCircle2 size={12} strokeWidth={3} /> Approve
                    </Btn>
                  )}
                  {(m.status === "approved" || m.status === "featured") && (
                    <Btn variant="ghost" onClick={() => moderate(m.id, m.status === "featured" || m.featured ? "unfeature" : "feature")} disabled={actingId === m.id} data-testid={`act-feature-${m.id}`}>
                      {(m.status === "featured" || m.featured) ? <><StarOff size={12} strokeWidth={3} /> Unfeature</> : <><Star size={12} strokeWidth={3} /> Feature</>}
                    </Btn>
                  )}
                  {m.status !== "spam" && (
                    <Btn variant="ghost" onClick={() => moderate(m.id, "spam")} disabled={actingId === m.id} data-testid={`act-spam-${m.id}`}>
                      <Ban size={12} strokeWidth={3} /> Spam
                    </Btn>
                  )}
                  {m.status !== "removed" && (
                    <Btn variant="danger" onClick={() => moderate(m.id, "remove")} disabled={actingId === m.id} data-testid={`act-remove-${m.id}`}>
                      <Trash2 size={12} strokeWidth={3} /> Remove
                    </Btn>
                  )}
                  {(m.status === "removed" || m.status === "spam") && (
                    <Btn variant="ghost" onClick={() => moderate(m.id, "restore")} disabled={actingId === m.id} data-testid={`act-restore-${m.id}`}>
                      <RotateCcw size={12} strokeWidth={3} /> Restore
                    </Btn>
                  )}
                </div>
              </li>
            ))}
          </ul>
          <Pagination skip={skip} limit={limit} total={data.total || 0} onChange={setSkip} />
        </Card>
      )}
    </div>
  );
};

export default Messages;
